import { useEffect, useMemo, useState } from 'react';
import { useThemeMode } from './ThemeContext';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    useMediaQuery,
    useTheme,
    Divider,
    FormControlLabel,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Skeleton,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PhonelinkIcon from '@mui/icons-material/Phonelink';
import {
    CredentialListItem,
    useDeleteWebAuthnCredential,
    useRegisterWebAuthnCredential,
    useWebAuthnCredentials,
} from '../hooks/webauthn';
import { useAlert } from './AlertContext';
import { isWebAuthnSecureContext, webAuthnDisabledReason } from '../utils/webauthn';
import { useChangePassword, useDeleteAccount } from '../hooks/account';
import {
    getCurrentPushSubscription,
    getPushSupport,
    subscribeToPush,
    unsubscribeFromPush,
    useNotificationPreferences,
    useUpdateNotificationPreferences,
} from '../hooks/notificationPreferences';
import { NotificationTypeKey, NOTIFICATION_TYPE_KEYS } from '../Interfaces/INotificationPreferences';
import { useProfile, useUpdateProfile } from '../hooks/profile';
import {
    extractApiDetail,
    useCancelEmailChange,
    useEmailChangeStatus,
    useRequestEmailChange,
} from '../hooks/emailChange';
import { fieldSx, primaryButtonSx, secondaryButtonSx } from './pvtaStyles';

type PrivateSettingsDialogProps = {
    open: boolean;
    onClose: () => void;
};

const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error && error.message.trim()) return error.message;
    return 'Unbekannter Fehler';
};

const formatDate = (value: string | null | undefined): string => {
    if (!value) return 'Nie';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short' }).format(parsed);
};

const NOTIFICATION_LABELS: Record<string, string> = {
    new_event: 'Neue Veranstaltung',
    event_updated: 'Veranstaltung aktualisiert',
    new_blog_post: 'Neuer Blogbeitrag',
    blog_comment_reply: 'Antwort auf Blog-Kommentar',
    event_comment_reply: 'Antwort auf Veranstaltungs-Kommentar',
    reservation_updated: 'Reservierung bearbeitet',
    new_rundbrief: 'Neue Rundmail',
};

// ── Passkeys Tab ────────────────────────────────────────────────────────────

function PasskeysTab({ open }: { open: boolean }) {
    const { showAlert } = useAlert();
    const [nickname, setNickname] = useState('');
    const [credentialToDelete, setCredentialToDelete] = useState<CredentialListItem | null>(null);
    const webAuthnEnabled = useMemo(() => isWebAuthnSecureContext(), []);

    const { data: credentials = [], isLoading, refetch } = useWebAuthnCredentials(open);
    const registerMutation = useRegisterWebAuthnCredential();
    const deleteMutation = useDeleteWebAuthnCredential();
    const isMutating = registerMutation.isPending || deleteMutation.isPending;

    const handleAdd = async () => {
        try {
            await registerMutation.mutateAsync({ nickname: nickname.trim() || undefined });
            setNickname('');
            await refetch();
            showAlert({ severity: 'success', text: 'Passkey erfolgreich hinzugefügt.' });
        } catch (error) {
            showAlert({ severity: 'error', text: `Passkey konnte nicht erstellt werden: ${getErrorMessage(error)}` });
        }
    };

    const handleDelete = async () => {
        if (!credentialToDelete) return;
        try {
            await deleteMutation.mutateAsync(credentialToDelete.credentialId);
            setCredentialToDelete(null);
            await refetch();
            showAlert({ severity: 'success', text: 'Passkey wurde entfernt.' });
        } catch (error) {
            showAlert({ severity: 'error', text: `Passkey konnte nicht entfernt werden: ${getErrorMessage(error)}` });
        }
    };

    return (
        <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Melde dich mit einem Fingerabdruck, Gesichtsscan oder Geräte-PIN an – ohne Passwort.
            </Typography>

            {!webAuthnEnabled && (
                <Alert severity="warning" sx={{ mb: 2 }}>{webAuthnDisabledReason}</Alert>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
                <TextField
                    label="Nickname (optional)"
                    size="small"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    fullWidth
                    sx={fieldSx}
                />
                <Tooltip title={!webAuthnEnabled ? webAuthnDisabledReason : ''}>
                    <span>
                        <Button sx={{ ...primaryButtonSx }} onClick={handleAdd} disabled={!webAuthnEnabled || isMutating}>
                            Passkey hinzufügen
                        </Button>
                    </span>
                </Tooltip>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {isLoading ? (
                <List disablePadding>
                    {[0, 1].map((i) => (
                        <ListItem key={i} divider disableGutters>
                            <ListItemText
                                primary={<Skeleton variant="text" width="30%" />}
                                secondary={<Skeleton variant="text" width="50%" />}
                            />
                        </ListItem>
                    ))}
                </List>
            ) : credentials.length === 0 ? (
                <Typography color="text.secondary">Noch keine Passkeys vorhanden.</Typography>
            ) : (
                <List disablePadding>
                    {credentials.map((cred) => (
                        <ListItem
                            key={cred.credentialId}
                            divider
                            disableGutters
                            secondaryAction={
                                <IconButton edge="end" color="error" onClick={() => setCredentialToDelete(cred)} disabled={isMutating}>
                                    <DeleteOutlineIcon />
                                </IconButton>
                            }
                        >
                            <ListItemText
                                primary={cred.nickname?.trim() || 'Unbenannt'}
                                secondary={
                                    <Box sx={{ mt: 0.5 }}>
                                        <Typography variant="body2" color="text.secondary">Erstellt: {formatDate(cred.createdAt)}</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>Letzte Nutzung: {formatDate(cred.lastUsedAt)}</Typography>
                                        <Stack direction="row" spacing={1} flexWrap="wrap">
                                            {(cred.transports || []).map((t) => <Chip key={t} size="small" label={t} />)}
                                        </Stack>
                                    </Box>
                                }
                            />
                        </ListItem>
                    ))}
                </List>
            )}

            <Dialog open={credentialToDelete !== null} onClose={() => setCredentialToDelete(null)}>
                <DialogTitle>Passkey entfernen?</DialogTitle>
                <DialogContent>
                    <Typography>Möchtest du den Passkey &quot;{credentialToDelete?.nickname?.trim() || 'Unbenannt'}&quot; wirklich entfernen?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCredentialToDelete(null)} disabled={deleteMutation.isPending}>Abbrechen</Button>
                    <Button color="error" onClick={handleDelete} disabled={deleteMutation.isPending}>Entfernen</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

// ── Passwort Tab ────────────────────────────────────────────────────────────

function PasswordTab() {
    const { showAlert } = useAlert();
    const { changePassword, isChanging } = useChangePassword();
    const [form, setForm] = useState({ old_password: '', new_password: '', confirm: '' });
    const [error, setError] = useState('');

    const handleSubmit = async () => {
        setError('');
        if (form.new_password !== form.confirm) {
            setError('Die neuen Passwörter stimmen nicht überein.');
            return;
        }
        if (form.new_password.length < 8) {
            setError('Das neue Passwort muss mindestens 8 Zeichen lang sein.');
            return;
        }
        try {
            await changePassword({ old_password: form.old_password, new_password: form.new_password });
            setForm({ old_password: '', new_password: '', confirm: '' });
            showAlert({ severity: 'success', text: 'Passwort wurde geändert.' });
        } catch (err) {
            const msg = getErrorMessage(err);
            if (msg.includes('falsch')) setError('Das aktuelle Passwort ist falsch.');
            else setError(msg);
        }
    };

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Stack spacing={2} sx={{ maxWidth: 400, width: '100%' }}>
            <Typography variant="body2" color="text.secondary">
                Wähle ein sicheres Passwort mit mindestens 8 Zeichen.
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
                label="Aktuelles Passwort"
                type="password"
                size="small"
                value={form.old_password}
                onChange={(e) => setForm((p) => ({ ...p, old_password: e.target.value }))}
                autoComplete="current-password"
                sx={fieldSx}
            />
            <TextField
                label="Neues Passwort"
                type="password"
                size="small"
                value={form.new_password}
                onChange={(e) => setForm((p) => ({ ...p, new_password: e.target.value }))}
                autoComplete="new-password"
                sx={fieldSx}
            />
            <TextField
                label="Neues Passwort bestätigen"
                type="password"
                size="small"
                value={form.confirm}
                onChange={(e) => setForm((p) => ({ ...p, confirm: e.target.value }))}
                autoComplete="new-password"
                sx={fieldSx}
            />
            <Button sx={{ ...primaryButtonSx }} onClick={handleSubmit} disabled={isChanging || !form.old_password || !form.new_password}>
                Passwort ändern
            </Button>
        </Stack>
        </Box>
    );
}

// ── Benachrichtigungen Tab ──────────────────────────────────────────────────

function NotificationsTab() {
    const { showAlert } = useAlert();
    const { preferences, isLoading } = useNotificationPreferences();
    const { updatePreferences, isUpdating } = useUpdateNotificationPreferences();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [pushSupported, setPushSupported] = useState(false);
    const [pushSubscribed, setPushSubscribed] = useState(false);
    const [pushLoading, setPushLoading] = useState(false);

    useEffect(() => {
        void getPushSupport().then(setPushSupported);
        void getCurrentPushSubscription().then((sub) => setPushSubscribed(sub !== null));
    }, []);

    const handleToggle = async (field: string, value: boolean) => {
        try {
            await updatePreferences({ [field]: value });
        } catch {
            showAlert({ severity: 'error', text: 'Einstellung konnte nicht gespeichert werden.' });
        }
    };

    const handlePushToggle = async () => {
        setPushLoading(true);
        try {
            if (pushSubscribed) {
                await unsubscribeFromPush();
                setPushSubscribed(false);
                showAlert({ text: 'Push-Benachrichtigungen deaktiviert.' });
            } else {
                const permission = await Notification.requestPermission();
                if (permission !== 'granted') {
                    showAlert({ severity: 'warning', text: 'Berechtigung für Benachrichtigungen wurde verweigert.' });
                    return;
                }
                const sub = await subscribeToPush();
                setPushSubscribed(sub !== null);
                if (sub) showAlert({ severity: 'success', text: 'Push-Benachrichtigungen aktiviert.' });
                else showAlert({ severity: 'error', text: 'Push-Benachrichtigungen konnten nicht aktiviert werden. VAPID-Key fehlt?' });
            }
        } catch (err) {
            showAlert({ severity: 'error', text: getErrorMessage(err) });
        } finally {
            setPushLoading(false);
        }
    };

    if (isLoading || !preferences) {
        return <Stack spacing={1}>{[0, 1, 2].map((i) => <Skeleton key={i} height={40} />)}</Stack>;
    }

    return (
        <Stack spacing={3}>
            {/* Push aktivieren */}
            <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <PhonelinkIcon fontSize="small" color="action" />
                    <Typography variant="subtitle2" fontWeight={600}>Push-Benachrichtigungen</Typography>
                </Stack>
                {!pushSupported ? (
                    <Alert severity="info" sx={{ mb: 1 }}>
                        Dein Browser unterstützt keine Push-Benachrichtigungen, oder die App läuft nicht als PWA.
                    </Alert>
                ) : (
                    <FormControlLabel
                        control={
                            <Switch
                                checked={pushSubscribed}
                                onChange={handlePushToggle}
                                disabled={pushLoading}
                            />
                        }
                        label={pushSubscribed ? 'Aktiv auf diesem Gerät' : 'Auf diesem Gerät aktivieren'}
                    />
                )}
            </Box>

            <Divider />

            {/* In-App */}
            <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <NotificationsIcon fontSize="small" color="action" />
                    <Typography variant="subtitle2" fontWeight={600}>Benachrichtigungen pro Typ</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Push ist nur wirksam wenn oben aktiviert. E-Mail wird an deine hinterlegte Adresse gesendet.
                </Typography>

                {isMobile ? (
                    // ── Mobile: pro Typ eine Zeile mit Name + 3 kompakten Toggles ──
                    <Stack divider={<Divider />}>
                        {NOTIFICATION_TYPE_KEYS.map((key: NotificationTypeKey) => {
                            const inappField = `inapp_${key}` as keyof typeof preferences;
                            const pushField  = `push_${key}`  as keyof typeof preferences;
                            const emailField = `email_${key}` as keyof typeof preferences;
                            return (
                                <Box key={key} sx={{ py: 1.25 }}>
                                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                                        {NOTIFICATION_LABELS[key] ?? key}
                                    </Typography>
                                    <Stack direction="row" spacing={2} justifyContent="center">
                                        <FormControlLabel
                                            labelPlacement="bottom"
                                            label={<Typography variant="caption" color="text.secondary">In-App</Typography>}
                                            control={<Switch size="small" checked={!!preferences[inappField]} onChange={(e) => { void handleToggle(inappField, e.target.checked); }} disabled={isUpdating} />}
                                            sx={{ mx: 0, gap: 0.25 }}
                                        />
                                        <FormControlLabel
                                            labelPlacement="bottom"
                                            label={<Typography variant="caption" color="text.secondary">Push</Typography>}
                                            control={<Switch size="small" checked={!!preferences[pushField]} onChange={(e) => { void handleToggle(pushField, e.target.checked); }} disabled={isUpdating || !pushSupported || !pushSubscribed} />}
                                            sx={{ mx: 0, gap: 0.25 }}
                                        />
                                        <FormControlLabel
                                            labelPlacement="bottom"
                                            label={<Typography variant="caption" color="text.secondary">E-Mail</Typography>}
                                            control={<Switch size="small" checked={!!preferences[emailField]} onChange={(e) => { void handleToggle(emailField, e.target.checked); }} disabled={isUpdating} />}
                                            sx={{ mx: 0, gap: 0.25 }}
                                        />
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Stack>
                ) : (
                    // ── Desktop: Grid mit Spalten ──────────────────────────────────
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 70px 70px 70px', gap: 0, alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ pb: 1 }} />
                        <Typography variant="caption" color="text.secondary" align="center" sx={{ pb: 1 }}>In-App</Typography>
                        <Typography variant="caption" color="text.secondary" align="center" sx={{ pb: 1 }}>Push</Typography>
                        <Typography variant="caption" color="text.secondary" align="center" sx={{ pb: 1 }}>E-Mail</Typography>

                        {NOTIFICATION_TYPE_KEYS.map((key: NotificationTypeKey) => {
                            const inappField = `inapp_${key}` as keyof typeof preferences;
                            const pushField  = `push_${key}`  as keyof typeof preferences;
                            const emailField = `email_${key}` as keyof typeof preferences;
                            return [
                                <Typography key={`${key}-label`} variant="body2" sx={{ py: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                                    {NOTIFICATION_LABELS[key] ?? key}
                                </Typography>,
                                <Box key={`${key}-inapp`} sx={{ display: 'flex', justifyContent: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
                                    <Switch size="small" checked={!!preferences[inappField]} onChange={(e) => { void handleToggle(inappField, e.target.checked); }} disabled={isUpdating} />
                                </Box>,
                                <Box key={`${key}-push`} sx={{ display: 'flex', justifyContent: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
                                    <Switch size="small" checked={!!preferences[pushField]} onChange={(e) => { void handleToggle(pushField, e.target.checked); }} disabled={isUpdating || !pushSupported || !pushSubscribed} />
                                </Box>,
                                <Box key={`${key}-email`} sx={{ display: 'flex', justifyContent: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
                                    <Switch size="small" checked={!!preferences[emailField]} onChange={(e) => { void handleToggle(emailField, e.target.checked); }} disabled={isUpdating} />
                                </Box>,
                            ];
                        })}
                    </Box>
                )}
            </Box>
        </Stack>
    );
}

// ── Account Tab ─────────────────────────────────────────────────────────────

// ── Profil Tab ──────────────────────────────────────────────────────────────

const PROFILE_FIELDS: Array<{ key: string; label: string; type?: string }> = [
    { key: 'first_name', label: 'Vorname' },
    { key: 'last_name', label: 'Nachname' },
    // E-Mail wird NICHT hier bearbeitet – Änderung nur über den verifizierten
    // Flow im Account-Tab (EmailChangeSection).
    { key: 'telefon', label: 'Telefon', type: 'tel' },
    { key: 'mobil', label: 'Mobil', type: 'tel' },
    { key: 'strasse', label: 'Straße' },
    { key: 'hausnummer', label: 'Hausnummer' },
    { key: 'plz', label: 'PLZ' },
    { key: 'stadt', label: 'Stadt' },
    { key: 'land', label: 'Land' },
    { key: 'beruf', label: 'Beruf' },
    { key: 'arbeitgeber', label: 'Arbeitgeber' },
    { key: 'biernamen', label: 'Bierename' },
    { key: 'aemter', label: 'Ämter' },
    { key: 'webseite', label: 'Webseite', type: 'url' },
    { key: 'linkedin', label: 'LinkedIn', type: 'url' },
    { key: 'instagram', label: 'Instagram' },
    { key: 'xing', label: 'XING', type: 'url' },
    { key: 'facebook', label: 'Facebook', type: 'url' },
    { key: 'geburtstag', label: 'Geburtstag', type: 'date' },
    { key: 'eintritt', label: 'Eintrittsjahr', type: 'date' },
];

function ProfileTab() {
    const { showAlert } = useAlert();
    const { profile, isLoading } = useProfile();
    const { updateProfile, isUpdating } = useUpdateProfile();
    const [form, setForm] = useState<Record<string, string>>({});
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        if (profile) {
            const initial: Record<string, string> = {};
            PROFILE_FIELDS.forEach(({ key }) => {
                initial[key] = (profile as Record<string, unknown>)[key] as string ?? '';
            });
            setForm(initial);
            setDirty(false);
        }
    }, [profile]);

    const handleChange = (key: string, value: string) => {
        setForm((p) => ({ ...p, [key]: value }));
        setDirty(true);
    };

    const handleSave = async () => {
        try {
            await updateProfile(form as Parameters<typeof updateProfile>[0]);
            setDirty(false);
            showAlert({ severity: 'success', text: 'Profil gespeichert.' });
        } catch (err) {
            showAlert({ severity: 'error', text: getErrorMessage(err) });
        }
    };

    if (isLoading) return <Stack spacing={1}>{[0,1,2,3].map((i) => <Skeleton key={i} height={56} />)}</Stack>;

    return (
        <Stack spacing={2}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { sm: '1fr 1fr' }, gap: 2 }}>
                {PROFILE_FIELDS.map(({ key, label, type }) => (
                    <TextField
                        key={key}
                        label={label}
                        type={type ?? 'text'}
                        size="small"
                        value={form[key] ?? ''}
                        onChange={(e) => handleChange(key, e.target.value)}
                        InputLabelProps={type === 'date' ? { shrink: true } : undefined}
                        sx={fieldSx}
                    />
                ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button sx={{ ...primaryButtonSx }} onClick={handleSave} disabled={isUpdating || !dirty}>
                    Profil speichern
                </Button>
            </Box>
        </Stack>
    );
}

// ── E-Mail-Änderung (verifiziert) ────────────────────────────────────────────

function EmailChangeSection() {
    const { showAlert } = useAlert();
    const { status, isLoading } = useEmailChangeStatus();
    const { requestEmailChange, isRequesting } = useRequestEmailChange();
    const { cancelEmailChange, isCanceling } = useCancelEmailChange();
    const [newEmail, setNewEmail] = useState('');
    const [error, setError] = useState('');

    const pending = status?.pending ?? false;

    const handleRequest = async () => {
        setError('');
        try {
            await requestEmailChange(newEmail.trim());
            setNewEmail('');
            showAlert({ severity: 'success', text: 'Bestätigungs-E-Mail an die neue Adresse gesendet.' });
        } catch (err) {
            setError(extractApiDetail(err) ?? 'Änderung konnte nicht beantragt werden.');
        }
    };

    const handleCancel = async () => {
        setError('');
        try {
            await cancelEmailChange();
            showAlert({ severity: 'info', text: 'Ausstehende E-Mail-Änderung abgebrochen.' });
        } catch (err) {
            setError(extractApiDetail(err) ?? 'Abbrechen fehlgeschlagen.');
        }
    };

    return (
        <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                E-Mail-Adresse ändern
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {status?.current_email ? (
                    <>Aktuelle Adresse: <strong>{status.current_email}</strong>. </>
                ) : null}
                Die neue Adresse wird erst nach Bestätigung über einen Link an die neue Adresse aktiv.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

            {pending && (
                <Alert
                    severity="info"
                    sx={{ mb: 1.5 }}
                    action={
                        <Button color="inherit" size="small" onClick={handleCancel} disabled={isCanceling}>
                            Abbrechen
                        </Button>
                    }
                >
                    Bestätigung ausstehend für <strong>{status?.new_email}</strong>. Bitte prüfe das Postfach
                    dieser Adresse. Du kannst unten eine neue Bestätigung anfordern.
                </Alert>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap>
                <TextField
                    label="Neue E-Mail-Adresse"
                    type="email"
                    size="small"
                    fullWidth
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    autoComplete="email"
                    sx={fieldSx}
                />
                <Button
                    sx={{ ...primaryButtonSx, whiteSpace: 'nowrap' }}
                    onClick={handleRequest}
                    disabled={isRequesting || isLoading || !newEmail.trim()}
                >
                    {pending ? 'Erneut anfordern' : 'Änderung anfordern'}
                </Button>
            </Stack>
        </Box>
    );
}

// ── Account Tab ─────────────────────────────────────────────────────────────

function AccountTab() {
    const { showAlert } = useAlert();
    const { deleteAccount, isDeleting } = useDeleteAccount();
    const [password, setPassword] = useState('');
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [error, setError] = useState('');
    const [isExporting, setIsExporting] = useState(false);

    const handleDelete = async () => {
        setError('');
        try {
            await deleteAccount({ password });
            showAlert({ severity: 'success', text: 'Account wurde gelöscht. Du wirst abgemeldet.' });
            setTimeout(() => window.location.reload(), 1500);
        } catch (err) {
            const msg = getErrorMessage(err);
            if (msg.includes('falsch')) setError('Passwort ist falsch.');
            else setError(msg);
        }
    };

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const res = await fetch('/api/accounts/export/', { credentials: 'include' });
            if (!res.ok) throw new Error('Export fehlgeschlagen');
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'meine-daten-pvta.zip';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            showAlert({ severity: 'error', text: 'Datenexport konnte nicht erstellt werden.' });
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Stack spacing={3}>
            <EmailChangeSection />

            <Divider />

            <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                    Meine Daten exportieren (DSGVO)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Lade eine ZIP-Datei mit deinem Profil, Reservierungen, Blogkommentaren und Benachrichtigungen herunter.
                </Typography>
                <Button
                    sx={secondaryButtonSx}
                    onClick={handleExport}
                    disabled={isExporting}
                    startIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                    {isExporting ? 'Wird erstellt …' : 'Daten herunterladen'}
                </Button>
            </Box>

            <Divider />

            <Alert severity="warning">
                Diese Aktionen sind <strong>nicht umkehrbar</strong>. Deine Daten werden anonymisiert und du wirst ausgeloggt.
            </Alert>

            <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    Account löschen
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Dein Account wird deaktiviert. Name und E-Mail werden anonymisiert. Bestehende Inhalte (Blogbeiträge, Kommentare) bleiben als &quot;Gelöschter Account&quot; erhalten.
                </Typography>
                <Button variant="outlined" color="error" onClick={() => setConfirmOpen(true)}>
                    Account löschen
                </Button>
            </Box>

            <Dialog open={confirmOpen} onClose={() => { setConfirmOpen(false); setError(''); setPassword(''); }}>
                <DialogTitle>Account wirklich löschen?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        Gib dein Passwort ein um zu bestätigen. Diese Aktion kann nicht rückgängig gemacht werden.
                    </Typography>
                    {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
                    <TextField
                        label="Passwort"
                        type="password"
                        size="small"
                        fullWidth
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="current-password"
                        sx={fieldSx}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setConfirmOpen(false); setError(''); setPassword(''); }} disabled={isDeleting}>
                        Abbrechen
                    </Button>
                    <Button color="error" onClick={handleDelete} disabled={isDeleting || !password}>
                        Endgültig löschen
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}

// ── Main Dialog ─────────────────────────────────────────────────────────────

export const PrivateSettingsDialog = ({ open, onClose }: PrivateSettingsDialogProps) => {
    const [tab, setTab] = useState(0);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { mode, toggleMode } = useThemeMode();

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>Einstellungen</span>
                    <Tooltip title={mode === 'dark' ? 'Hell-Modus' : 'Dunkel-Modus'}>
                        <IconButton onClick={toggleMode} size="small">
                            {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                        </IconButton>
                    </Tooltip>
                </Box>
            </DialogTitle>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v as number)} variant="scrollable" scrollButtons="auto">
                    <Tab label="Benachrichtigungen" />
                    <Tab label="Passwort" />
                    <Tab label="Passkeys" />
                    <Tab label="Account" />
                </Tabs>
            </Box>
            <DialogContent dividers sx={{ minHeight: 300 }}>
                {tab === 0 && <NotificationsTab />}
                {tab === 1 && <PasswordTab />}
                {tab === 2 && <PasskeysTab open={open} />}
                {tab === 3 && <AccountTab />}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Schließen</Button>
            </DialogActions>
        </Dialog>
    );
};
